import { useCallback, useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Camera, RotateCcw, Check, X, AlertCircle, Loader2, MapPinOff } from 'lucide-react';
import { useGeolocation, useCamera } from '@/lib/contexts/device-permissions-context';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { useTranslation } from 'react-i18next';

/**
 * Sheet pour capturer une photo horodatée via la caméra (champ de procédure photo).
 *
 * Payload renvoyé à `onCapture` :
 *   { mediaType: 'photo', mimeType, base64, fileName, dataUrl }
 */
export default function PhotoCaptureSheet({ open, onClose, onCapture, isUploading = false }) {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const {
        isEnabled: isGeolocationEnabled,
        toggleGeolocation,
        permissionStatus: geoPermissionStatus,
    } = useGeolocation();

    const { getStream: getCameraStream, isSupported: isCameraSupported } = useCamera();

    const [cameraStatus, setCameraStatus] = useState('pending');
    const [errorMessage, setErrorMessage] = useState('');
    const [capturedImage, setCapturedImage] = useState(null);
    const [facingMode, setFacingMode] = useState('environment');
    const [isStartingCamera, setIsStartingCamera] = useState(false);

    const isGeolocationActive = isGeolocationEnabled && geoPermissionStatus === 'granted';
    const hasCaptured = !!capturedImage;

    const startCamera = useCallback(async () => {
        if (!isGeolocationActive) return;

        if (!isCameraSupported) {
            setCameraStatus('error');
            setErrorMessage("La caméra n'est pas disponible sur cet appareil.");
            return;
        }

        setIsStartingCamera(true);
        setErrorMessage('');

        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }

            const constraints = {
                video: {
                    facingMode,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: false,
            };

            const stream = await getCameraStream(constraints);

            if (!stream) {
                setCameraStatus('denied');
                setErrorMessage(
                    "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.",
                );
                return;
            }

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }

            setCameraStatus('granted');
        } catch (err) {
            console.error('[PhotoCaptureSheet] Erreur accès caméra:', err);

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setCameraStatus('denied');
                setErrorMessage(
                    "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.",
                );
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                setCameraStatus('error');
                setErrorMessage('Aucune caméra détectée sur cet appareil.');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                setCameraStatus('error');
                setErrorMessage('La caméra est utilisée par une autre application.');
            } else {
                setCameraStatus('error');
                setErrorMessage("Impossible d'accéder à la caméra. Vérifiez vos paramètres.");
            }
        } finally {
            setIsStartingCamera(false);
        }
    }, [facingMode, isGeolocationActive, isCameraSupported, getCameraStream]);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    const capturePhoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setCapturedImage(imageDataUrl);

        stopCamera();
    }, [stopCamera]);

    const resetCapture = useCallback(() => {
        setCapturedImage(null);
    }, []);

    const retake = useCallback(() => {
        resetCapture();
        startCamera();
    }, [resetCapture, startCamera]);

    const toggleCamera = useCallback(() => {
        setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
    }, []);

    const confirmCapture = useCallback(() => {
        if (!capturedImage) return;
        const base64Data = capturedImage.split(',')[1];
        const fileName = `photo_${Date.now()}.jpg`;
        onCapture({
            mediaType: 'photo',
            mimeType: 'image/jpeg',
            base64: base64Data,
            fileName,
            dataUrl: capturedImage,
        });
    }, [capturedImage, onCapture]);

    const handleClose = useCallback(() => {
        if (isUploading) return;
        stopCamera();
        resetCapture();
        setCameraStatus('pending');
        setErrorMessage('');
        onClose();
    }, [stopCamera, resetCapture, onClose, isUploading]);

    useEffect(() => {
        if (open && !hasCaptured && isGeolocationActive) {
            const timer = setTimeout(() => startCamera(), 100);
            return () => clearTimeout(timer);
        }

        if (!open) {
            stopCamera();
            resetCapture();
            setCameraStatus('pending');
            setErrorMessage('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, hasCaptured, isGeolocationActive]);

    useEffect(() => {
        if (open && cameraStatus === 'granted' && !hasCaptured) {
            startCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facingMode]);

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && !isUploading && handleClose()}>
            <SheetContent className="w-full! max-w-full! md:w-[600px]! md:max-w-[600px]! flex flex-col p-0 h-full">
                <SheetHeader className="px-6 py-4 border-b border-border shrink-0">
                    <SheetTitle className="text-lg font-semibold text-foreground">
                        {t('procedures:prendreUnePhotoHorodatee')}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 flex flex-col items-center justify-center bg-foreground-dark relative overflow-hidden">
                    {!isGeolocationActive && (
                        <div className="flex flex-col items-center justify-center gap-4 text-background p-6 text-center">
                            <div className="w-16 h-16 bg-warning/20 rounded-full flex items-center justify-center">
                                <MapPinOff className="w-10 h-10 text-warning" />
                            </div>
                            <p className="text-base font-medium">{t('procedures:geolocalisationRequise')}</p>
                            <p className="text-sm text-background/70 max-w-xs">
                                {t('procedures:pourPrendreUnePhotoHorodateeVousDevezActiver')}
                            </p>
                            <Button
                                variant="soft"
                                color="secondary"
                                onClick={toggleGeolocation}
                                className="mt-2 text-primary-foreground"
                            >
                                <MapPinOff className="w-4 h-4" />
                                {t('procedures:activerLaGeolocalisation')}
                            </Button>
                        </div>
                    )}

                    {isGeolocationActive && (cameraStatus === 'pending' || isStartingCamera) && !hasCaptured && (
                        <div className="flex flex-col items-center justify-center gap-4 text-background">
                            <Loader2 className="w-12 h-12 animate-spin" />
                            <p className="text-sm">{t('procedures:accesALaCamera')}</p>
                        </div>
                    )}

                    {isGeolocationActive &&
                        (cameraStatus === 'denied' || cameraStatus === 'error') &&
                        !isStartingCamera &&
                        !hasCaptured && (
                            <div className="flex flex-col items-center justify-center gap-4 text-background p-6 text-center">
                                <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-10 h-10 text-destructive" />
                                </div>
                                <p className="text-sm max-w-xs">{errorMessage}</p>
                                <Button variant="soft" color="secondary" onClick={startCamera} className="mt-2">
                                    <RotateCcw className="w-4 h-4" />
                                    {t('procedures:reessayer')}
                                </Button>
                            </div>
                        )}

                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`w-full h-full object-contain ${
                            isGeolocationActive && cameraStatus === 'granted' && !hasCaptured && !isStartingCamera
                                ? ''
                                : 'hidden'
                        }`}
                    />

                    {isGeolocationActive &&
                        cameraStatus === 'granted' &&
                        !hasCaptured &&
                        !isStartingCamera && (
                            <Button
                                variant="plain"
                                color="neutral"
                                size="auto"
                                onClick={toggleCamera}
                                className="absolute top-4 right-4 w-10 h-10 bg-foreground-dark/50 rounded-full flex items-center justify-center text-background hover:bg-foreground-dark/70 transition-colors"
                                title={t('procedures:changerDeCamera')}
                            >
                                <RotateCcw className="w-5 h-5" />
                            </Button>
                        )}

                    {isGeolocationActive && capturedImage && (
                        <img src={capturedImage} alt={t('procedures:photoCapturee')} className="w-full h-full object-contain" />
                    )}

                    <canvas ref={canvasRef} className="hidden" />
                </div>

                <SheetFooter className="px-6 py-4 border-t border-border shrink-0">
                    {!isGeolocationActive ? (
                        <div className="flex items-center justify-center w-full">
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                onClick={handleClose}
                                disabled={isUploading}
                            >
                                <X className="w-4 h-4" />
                                {t('procedures:fermer')}
                            </Button>
                        </div>
                    ) : !hasCaptured ? (
                        <div className="flex items-center justify-center w-full gap-4">
                            <Button
                                type="button"
                                variant="soft"
                                color="neutral"
                                onClick={handleClose}
                                disabled={isUploading}
                            >
                                <X className="w-4 h-4" />
                                {t('procedures:annuler')}
                            </Button>

                            <SubmitButton
                                type="button"
                                onClick={capturePhoto}
                                disabled={cameraStatus !== 'granted' || isStartingCamera}
                            >
                                <Camera className="w-5 h-5" />
                                {t('procedures:capturer')}
                            </SubmitButton>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center w-full gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                onClick={retake}
                                disabled={isUploading}
                            >
                                <RotateCcw className="w-4 h-4" />
                                {t('procedures:reprendre')}
                            </Button>
                            <SubmitButton
                                type="button"
                                onClick={confirmCapture}
                                isSubmitting={isUploading}
                                loadingText={t('procedures:envoi2')}
                            >
                                <Check className="w-5 h-5" />
                                {t('procedures:valider')}
                            </SubmitButton>
                        </div>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
